import { jsClasses } from '@/helpers/settings'; 
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'; 

let Modal = (({}) => { 
    class Modal { 
        constructor(node, options = {}) { 
            this.node = node; 
            this.window = window; 
            this.settings = this.getSettings(options); 
            this.pluginOptions = { reserveScrollBarGap: true, }; 
            this.data = this.node.dataset; 
            
            this.modalId = this.data.modalId || this.node.id;
            this.listeners = document.querySelectorAll(`[data-modal-target="${this.modalId}"]`); 
            this.overlay = this.node.querySelector('[data-js="overlay"]'); 
            this.onWindowClickBound = this.onWindowClick.bind(this); 
            
            // Corrected your original class method pointer name typo mapping
            this.onKeydownBound = this.onKeydown.bind(this); 
            
            this.closeEvent = new CustomEvent('modalClose'); 
            this.openEvent = new CustomEvent('modalOpen'); 
            this.justClicked = false; 
            this.animationFrameId = null; 
        } 

        getSettings(options = {}) { 
            return Object.assign( {}, { activeClass: jsClasses.open, }, options ); 
        } 

        getBooleanFromAction(action, modalIsActive = false) { 
            let manifest = { open: true, close: false, toggle: !modalIsActive, }; 
            return action in manifest ? manifest[action] : manifest.toggle; 
        } 

        jsFadeIn(duration = 300) {
            cancelAnimationFrame(this.animationFrameId);
            this.node.style.opacity = 0;
            this.node.style.display = 'block';
            
            let startTime = performance.now();
            const animate = (currentTime) => {
                let elapsed = currentTime - startTime;
                let progress = Math.min(elapsed / duration, 1);
                
                this.node.style.opacity = progress;
                
                if (progress < 1) {
                    this.animationFrameId = requestAnimationFrame(animate);
                }
            };
            this.animationFrameId = requestAnimationFrame(animate);
        }

        jsFadeOut(duration = 300, callback) {
            cancelAnimationFrame(this.animationFrameId);
            let startOpacity = parseFloat(this.node.style.opacity) || 1;
            let startTime = performance.now();
            
            const animate = (currentTime) => {
                let elapsed = currentTime - startTime;
                let progress = Math.min(elapsed / duration, 1);
                
                this.node.style.opacity = startOpacity * (1 - progress);
                
                if (progress < 1) {
                    this.animationFrameId = requestAnimationFrame(animate);
                } else {
                    this.node.style.display = 'none';
                    if (typeof callback === 'function') callback();
                }
            };
            this.animationFrameId = requestAnimationFrame(animate);
        }

        toggle(shouldOpen) { 
            if (this.justClicked) return; 

            if (shouldOpen) {
                this.node.setAttribute('aria-hidden', 'false'); 
                this.node.classList.add(this.settings.activeClass); 
                
                this.jsFadeIn(300);
                disableBodyScroll(this.node, this.pluginOptions);
            } else {
                this.jsFadeOut(300, () => {
                    this.node.setAttribute('aria-hidden', 'true'); 
                    this.node.classList.remove(this.settings.activeClass); 
                    clearAllBodyScrollLocks();
                });
            }

            this.setModalOverlayEventBindings(shouldOpen); 
            
            let event = shouldOpen ? this.openEvent : this.closeEvent; 
            this.node.dispatchEvent(event); 
            
            this.justClicked = true; 
            setTimeout(() => { this.justClicked = false; }, 400); 
        } 

        onWindowClick(event) { 
            let target = event.target; 
            
            let overlayWasClicked = target === this.overlay || target.closest('[data-js="overlay"]') !== null; 
            if (!overlayWasClicked) return; 
            
            event.preventDefault();
            event.stopPropagation();
            this.toggle(false); 
        } 

        onKeydown(event) { 
            if ( event.key === 'Escape' ) { 
                this.toggle(false); 
            } 
        } 

        setModalOverlayEventBindings(shouldOpen) { 
            if (this.data.closeable == 'false') return; 
            if (!shouldOpen) { 
                this.window.removeEventListener('click', this.onWindowClickBound, true); 
                this.window.removeEventListener('keydown', this.onKeydownBound, true); 
                return; 
            } 
            
            this.window.addEventListener('click', this.onWindowClickBound, true); 
            this.window.addEventListener('keydown', this.onKeydownBound, true); 
        } 

        onClick(event) { 
            event.preventDefault(); 
            let modalIsActive = this.node.classList.contains(this.settings.activeClass); 
            let target = event.currentTarget; 
            let targetData = target.dataset; 
            let shouldOpen = this.getBooleanFromAction(targetData.action, modalIsActive); 
            this.toggle(shouldOpen); 
        } 

        handleServerSideOpen() { 
            if (this.node.classList.contains(this.settings.activeClass)) { 
                this.toggle(true); 
            } 
        } 

        setEventBindings() { 
            [...this.listeners].map((element) => { 
                element.addEventListener('click', this.onClick.bind(this)); 
            }); 
            this.node.addEventListener('requestClose', this.toggle.bind(this, false)); 

            const internalCloseButtons = this.node.querySelectorAll('.close, [data-action="close"]');
            [...internalCloseButtons].forEach((btn) => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggle(false);
                });
            });
        } 

        init() { 
            this.setEventBindings(); 
            this.handleServerSideOpen(); 
        } 
    } 

    return { 
        init({ selector }) { 
            let nodeList = document.querySelectorAll(selector); 
            if (!nodeList.length) return; 
            return [...nodeList].map((node) => { 
                let module = new Modal(node); 
                module.init(); 
                return module; 
            }); 
        }, 
    }; 
})(window); 

export default Object.create(Modal);
